// types.ts - Type definitions for better type safety
export interface SubModule {
  sub_module_name: string;
}

export interface ModuleAccess {
  module_name: string;
  sub_modules?: SubModule[];
}

export interface UserData {
  module_access?: ModuleAccess[];
}

export interface MenuItem {
  bacSubModuleName?: string | string[];
  submenu?: MenuItem[];
  [key: string]: any;
}

export interface Permission {
  [key: string]: any;
}

const REPORT_PERMISSION_KEY_MAP: Record<string, string> = {
  "hrms/reports": "reports-hrms",
  "inventory/reports": "reports-inventory",
  "sales/reports": "reports-sales",
  "rental/reports": "reports-rental",
  "manufacturing/reports": "reports-manufacturing",
  "purchase/reports": "reports-purchase",
  "accounts-and-finance/reports": "reports-accounts-and-finance",
};

const getPermissionOperations = (permissionEntry: any): string[] =>
  Object.values(permissionEntry || {})
    .flat()
    .map((p: any) => p.identity?.operation)
    .filter((operation: string | undefined): operation is string => Boolean(operation));

const getPermissionKeyForSubModule = (subModuleName: string): string =>
  REPORT_PERMISSION_KEY_MAP[subModuleName] ??
  subModuleName.split("/").pop() ??
  "";

const shouldExpandChildSubModules = (
  moduleName: string,
  subModuleName: string
): boolean => {
  return moduleName === "hrms" || subModuleName in REPORT_PERMISSION_KEY_MAP;
};

const hasNestedModulePath = (value: string | string[] | undefined): boolean => {
  if (!value) return false;

  if (Array.isArray(value)) {
    return value.some((item) => item.includes("/"));
  }

  return value.includes("/");
};

// storageUtils.ts - Centralized storage access
export class StorageUtils {
  /**
   * Retrieves and parses user data from localStorage
   * @returns Parsed user data or null if not found
   */
  static getUserData(): UserData | null {
    try {
      const userData = localStorage.getItem("_u_data");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  }

  /**
   * Retrieves and parses role permissions from localStorage
   * @returns Parsed permissions object
   */
  static getRolePermissions(): Permission {
    try {
      const permissions = localStorage.getItem("role-permission");
      return permissions ? JSON.parse(permissions) : {};
    } catch (error) {
      console.error("Error parsing role permissions:", error);
      return {};
    }
  }
}

// moduleAccessService.ts - Business logic for module access
export class ModuleAccessService {
  /**
   * Filters modules by allowed module names and adds dashboard sub-module
   * @param moduleNames - Array of allowed module names
   * @param userData - User data containing module access
   * @returns Filtered modules with dashboard added
   */
  static getAccessibleSubModules(
    moduleNames: string[],
    userData: UserData | null,
    permissions: Permission
  ): ModuleAccess[] {
    if (!userData?.module_access) {
      return [];
    }

    return userData.module_access
      .filter((module) => moduleNames.includes(module.module_name))
      .map((module) => {
        return {
          ...module,
          sub_modules: [
            ...(module.sub_modules || []).flatMap((subModule) => {
              if (!shouldExpandChildSubModules(module.module_name, subModule.sub_module_name)) {
                return [subModule];
              }

              const permissionKey = getPermissionKeyForSubModule(subModule.sub_module_name);
              const permissionEntry = permissions[permissionKey];
              const childSubModules = getPermissionOperations(permissionEntry).map(
                (operation): SubModule => ({
                  sub_module_name: `${subModule.sub_module_name}/${operation}`,
                })
              );

              return [subModule, ...childSubModules];
            }),
            { sub_module_name: "dashboard" },
          ],
        };
      });
  }

  /**
   * Extracts unique sub-module names from modules
   * @param modules - Array of module access objects
   * @returns Set of unique sub-module names
   */
  static extractSubModuleNames(modules: ModuleAccess[]): Set<string> {
    return new Set(
      modules.flatMap((module) => 
        (module.sub_modules || []).map((subModule) => subModule.sub_module_name)
      )
    );
  }

  /**
   * Matches sub-module names with permission keys
   * @param subModuleNames - Set of sub-module names
   * @param permissions - Permission object
   * @returns Set of matching sub-module names
   */
  static getMatchingSubModules(
    subModuleNames: Set<string>,
    permissions: Permission
  ): Set<string> {
    const permissionKeys = Object.keys(permissions);
    const _hrmsPermissionKeys = permissionKeys?.map(v => v.replace(/(\/-p.*$|-p$)/i, ""))
    const matchingModules = Array.from(subModuleNames).filter((subModuleName) => {
      const matchingReportKey = Object.entries(REPORT_PERMISSION_KEY_MAP).find(
        ([reportPath]) =>
          subModuleName === reportPath || subModuleName.startsWith(`${reportPath}/`)
      );

      if (matchingReportKey) {
        const [reportPath, permissionKey] = matchingReportKey;
        const reportOperations = getPermissionOperations(permissions[permissionKey]);
        if (!reportOperations.length) return false;

        const reportRoutePath = subModuleName.replace(`${reportPath}/`, "").replace(reportPath, "");
        if (!reportRoutePath) return true;

        return reportOperations.some(
          (operation) =>
            operation === reportRoutePath ||
            operation === ":nop" ||
            operation === ":param" ||
            operation === "*" ||
            (operation.endsWith("*") && reportRoutePath.startsWith(operation.slice(0, -1)))
        );
      }

      const segments = subModuleName.split("/");
      
      if(segments[0] === "hrms"){
        // Find the segment that exactly matches a permission key
        const matchedKeyIndex = segments.findIndex((segment) =>
          _hrmsPermissionKeys.includes(segment)
        );
        
        if (matchedKeyIndex === -1) return false;

        const matchedKey = segments[matchedKeyIndex];       // e.g. "payslips"
        const childSegment = segments[matchedKeyIndex + 1]; // e.g. "payslips/generate" or undefined

        // No child segment → top-level module access is enough
        if (!childSegment) return true;

        const permissionEntry = permissions[matchedKey];
        if (!permissionEntry) return false;

        // Collect all operations across r, w, d
        const allOperations = Object.values(permissionEntry)
          .flat()
          .map((p: any) => p.identity?.operation);

        return allOperations.some(
          (op) =>
            op === childSegment || // exact: "generate" === "generate"
            op === ":nop" ||       // wildcard: list operation
            op === ":param"        // wildcard: single resource operation
        );
      }else {
         const lastPart = subModuleName.split("/").pop() || "";
         return permissionKeys.some((permKey) => permKey.startsWith(lastPart));
      }
    });

    return new Set(matchingModules);
  }
}

// menuFilterService.ts - Menu filtering logic
export class MenuFilterService {
  private static checkSubModuleAccess(
    bacSubModuleName: string | string[] | undefined,
    accessibleSubModules: Set<string>
  ): boolean {
    if (!bacSubModuleName) return false;

    if (Array.isArray(bacSubModuleName)) {
      return bacSubModuleName.some((name) => name === "dashboard" || accessibleSubModules.has(name));
    }

    return bacSubModuleName === "dashboard" || accessibleSubModules.has(bacSubModuleName);
  }

  private static filterHrmsSubmenu(
    menu: MenuItem[],
    accessibleSubModules: Set<string>
  ): MenuItem[] {
    return menu
      .map((item) => {
        if (item.submenu && item.submenu.length > 0) {
          const filteredSubmenu = this.filterHrmsSubmenu(item.submenu, accessibleSubModules);
          const hasValidSubModule = this.checkSubModuleAccess(item.bacSubModuleName, accessibleSubModules);

          if (filteredSubmenu.length > 0 || hasValidSubModule) {
            return { ...item, submenu: filteredSubmenu };
          }
          return null;
        }
        // Leaf node
        return this.checkSubModuleAccess(item.bacSubModuleName, accessibleSubModules) ? item : null;
      })
      .filter((item): item is MenuItem => item !== null);
  }

  /**
   * Recursively filters menu items based on accessible sub-modules
   * @param menu - Array of menu items
   * @param accessibleSubModules - Set of accessible sub-module names
   * @returns Filtered menu array
   */
  static filterMenuByAccess(
    menu: MenuItem[],
    accessibleSubModules: Set<string>
  ): MenuItem[] {
    return menu.filter((item) => {
      const hasValidSubModule = this.checkSubModuleAccess(item.bacSubModuleName, accessibleSubModules);
      let hasValidSubmenu = false;
      if (item.submenu && item.submenu.length > 0) {
        // 👇 HRMS children → new logic; everything else → recurse with old logic
        item.submenu = item.submenu.some((child) =>
          hasNestedModulePath(child.bacSubModuleName)
        )
          ? this.filterHrmsSubmenu(item.submenu, accessibleSubModules)
          : this.filterMenuByAccess(item.submenu, accessibleSubModules);

        hasValidSubmenu = item.submenu.length > 0;
      }

      return hasValidSubModule || hasValidSubmenu;
    });
    // return menu.filter((item) => {
    //   // const hasValidSubModule = 
    //   //   item.bacSubModuleName && accessibleSubModules.has(item.bacSubModuleName);
    //   const hasValidSubModule = Array.isArray(item.bacSubModuleName)
    //         ? item.bacSubModuleName.some((name) => accessibleSubModules.has(name))
    //         : item.bacSubModuleName
    //           ? accessibleSubModules.has(item.bacSubModuleName)
    //           : false;
    //   let hasValidSubmenu = false;
    //   if (item.submenu && item.submenu.length > 0) {
    //     item.submenu = this.filterMenuByAccess(item.submenu, accessibleSubModules);
    //     hasValidSubmenu = item.submenu.length > 0;
    //   }
    //   return hasValidSubModule || hasValidSubmenu;
    // });
    //  return menu
    //   .map((item) => {
    //     // If item has a submenu, recursively filter it first
    //     if (item.submenu && item.submenu.length > 0) {
    //       const filteredSubmenu = this.filterMenuByAccess(item.submenu, accessibleSubModules);

    //       // Check if the item itself has a valid bacSubModuleName
    //       const hasValidSubModule = this.checkSubModuleAccess(item.bacSubModuleName, accessibleSubModules);

    //       // Keep item if:
    //       // 1. It has valid filtered children
    //       // 2. OR it has its own valid access AND no submenu children blocked it
    //       if (filteredSubmenu.length > 0 || hasValidSubModule) {
    //         return { ...item, submenu: filteredSubmenu };
    //       }

    //       return null; // drop item entirely
    //     }

    //     // Leaf node — just check its own bacSubModuleName
    //     const hasValidSubModule = this.checkSubModuleAccess(item.bacSubModuleName, accessibleSubModules);
    //     return hasValidSubModule ? item : null;
    //   })
    //   .filter((item): item is MenuItem => item !== null);
  }
}

// menuFilter.ts - Main filter orchestrator
export class MenuFilter {
  /**
   * Filters menu based on user permissions and module access
   * @param menu - Menu items to filter
   * @param allowedModules - Array of allowed module names
   * @returns Filtered menu items
   */
  static filterByModuleAccess(
    menu: MenuItem[],
    allowedModules: string[]
  ): MenuItem[] {
    // Retrieve user data and permissions
    const userData = StorageUtils.getUserData();
    const permissions = StorageUtils.getRolePermissions();
 
    // Get accessible sub-modules
    const accessibleModules = ModuleAccessService.getAccessibleSubModules(
      allowedModules,
      userData,
      permissions
    );
    // Extract sub-module names
    const subModuleNames = ModuleAccessService.extractSubModuleNames(
      accessibleModules
    );

    // Match with permissions
    const matchingSubModules = ModuleAccessService.getMatchingSubModules(
      subModuleNames,
      permissions
    );
    // Filter menu
    return MenuFilterService.filterMenuByAccess(menu, matchingSubModules);
  }
}

// Usage example in your module
// manufacturing.ts
// import { MenuFilter } from './menuFilter';
// import { MenuItem } from './types';

// export const getFilteredManufacturingMenu = (
//   menuData: MenuItem[]
// ): MenuItem[] => {
//   const allowedModules = ["manufacturing", "inventory", "sales"];
//   return MenuFilter.filterByModuleAccess(menuData, allowedModules);
// };

// // sales.ts
// export const getFilteredSalesMenu = (
//   menuData: MenuItem[]
// ): MenuItem[] => {
//   const allowedModules = ["sales", "inventory"];
//   return MenuFilter.filterByModuleAccess(menuData, allowedModules);
// };

// // Generic usage with dynamic modules
// export const getFilteredMenu = (
//   menuData: MenuItem[],
//   allowedModules: string[]
// ): MenuItem[] => {
//   return MenuFilter.filterByModuleAccess(menuData, allowedModules);
// };
